import React, { useState } from 'react';
import { FormattedMessage } from 'react-intl/dist';
import styled from 'styled-components';
import { useForm } from 'react-hook-form';

//components

import { Table } from 'app-components';

//type

import type { ArchiveSearchDetails } from './useArchiveHousehold';

//styles

const StyledSearchContainer = styled.div`
    padding-top: 5%;

    .searchText {
        font-size: 2.0rem;
    }

    .searchSubText {
        font-size: 1.7rem;

        .label {
            font-size: 3.0rem;
        }
    }

    .inputBox {
        display: flex;
        flex-direction: row;
        column-gap: 15%;
        padding-top: 3%;
        padding-bottom: 4%;
    }
`;

const StyledCheckbox = styled.div`
    /* The container */
    .container {
        position: relative;
        padding-left: 35px;
        margin-bottom: 12px;
        cursor: pointer;
        font-size: 1.8rem;
        -webkit-user-select: none;
        -moz-user-select: none;
        -ms-user-select: none;
        user-select: none;
    }

    /* Hide the browser's default checkbox */
    .container input {
        position: absolute;
        opacity: 0;
        cursor: pointer;
        height: 0;
        width: 0;
    }

    /* Create a custom checkbox */
    .checkmark {
        position: absolute;
        top: 0;
        left: 0;
        height: 25px;
        width: 25px;
        background-color: #eee;
    }

    /* On mouse-over, add a grey background color */
    .container:hover input ~ .checkmark {
        background-color: #ccc;
    }

    /* When the checkbox is checked, add a blue background */
    .container input:checked ~ .checkmark {
        background-color: #2196F3;
    }

    /* Create the checkmark/indicator (hidden when not checked) */
    .checkmark:after {
        content: "";
        position: absolute;
        display: none;
    }

    /* Show the checkmark when checked */
    .container input:checked ~ .checkmark:after {
        display: block;
    }

    /* Style the checkmark/indicator */
    .container .checkmark:after {
        left: 9px;
        top: 5px;
        width: 5px;
        height: 10px;
        border: solid white;
        border-width: 0 3px 3px 0;
        -webkit-transform: rotate(45deg);
        -ms-transform: rotate(45deg);
        transform: rotate(45deg);
    }
`;

//props

interface ArchiveHouseholdProps {
    data: ArchiveSearchDetails[] | null;
    isError: boolean;
    onSearch: (hhId: string) => void;
    onArchive: (householdDetails: ArchiveSearchDetails) => void;
    onUnArchive: (householdDetails: ArchiveSearchDetails) => void;
}

const ArchiveHousehold = (props: ArchiveHouseholdProps) => {
    const { register, handleSubmit, formState: { errors } } = useForm();
    const { data, isError, onArchive, onSearch: doSearch, onUnArchive } = props;

    const onSubmit = (values) => {
        doSearch(values.householdId);
    };
    
    const NoResultsFound = () => {
      return (
          <div>
              <FormattedMessage id='archive.hh.no_results_found' />
          </div>
      );
    };
      
    const renderArchiveButton = (item: ArchiveSearchDetails) => {
        return (
            <button className="usa-button gi-button gi-button__primary" type="submit" onClick={() => onArchive(item)}><FormattedMessage id='archive.hh.btn.archive' /></button>
        );
    };
        
    const renderUnArchiveButton = (item: ArchiveSearchDetails) => {
        return (
            <button className="usa-button gi-button gi-button__primary" type="submit" onClick={() => onUnArchive(item)}><FormattedMessage id='archive.hh.btn.unarchive' /></button>
        );
    };

    const renderButtons = (item: ArchiveSearchDetails) => {
        const {
            canUnarchived,
            isArchived
        } = item;

        if (canUnarchived) {
            return renderUnArchiveButton(item);
        }

        if (!isArchived) {
            return renderArchiveButton(item);
        }
    };

    const renderMatchCriteria = (item: ArchiveSearchDetails) => {
        const {
            applicationYears,
            enrollmentYears,
            AgentDesignated,
            isArchived,
            isInboxmsgPresent,
            username,
            location: {
              address1,
              address2,
              city,
              state,
              zip
            } = {}
        } = item;

        const address = address1 ? `${address1} ${address2 || ''}, ${city}, ${state} ${zip}` : <FormattedMessage id='archive.hh.match.agent_designated_none' />;

        return (
            <>
                { isArchived && <div><FormattedMessage id='archive.hh.match.abandoned_hh' /></div>}
                <p/>
                <div><FormattedMessage id='archive.hh.match.primary_contact' />{username}</div>
                <div><FormattedMessage id='archive.hh.match.primary_address' />{address}</div>
                <div>
                    <FormattedMessage id='archive.hh.match.application' />
                    {
                        applicationYears && applicationYears.length > 0 &&
                        applicationYears.map((item, idx) => {
                            return (
                                <>{item}{applicationYears.length-1 > idx ? ', ' : ''}</>
                            )
                        })
                    }
                    {
                        (!applicationYears || applicationYears.length < 1) && <FormattedMessage id='archive.hh.match.agent_designated_none' />
                    }
                </div>
                <div>
                    <FormattedMessage id='archive.hh.match.enrollment' />
                    {
                        enrollmentYears && enrollmentYears.length > 0 &&
                        enrollmentYears.map((item, idx) => {
                            return (
                                <>{item}{enrollmentYears.length-1 > idx ? ', ' : ''}</>
                            )
                        })
                    }
                    {
                        (!enrollmentYears || enrollmentYears.length < 1) && <FormattedMessage id='archive.hh.match.agent_designated_none' />
                    }
                </div>
                <div>
                    <FormattedMessage id='archive.hh.match.inbox_msgs' />{ isInboxmsgPresent ? <FormattedMessage id='archive.hh.match.yes' /> : <FormattedMessage id='archive.hh.match.no' /> }
                </div>
                <div>
                    <FormattedMessage id='archive.hh.match.agent_designated' /> { AgentDesignated ? AgentDesignated : <FormattedMessage id='archive.hh.match.agent_designated_none' />}
                </div>
            </>
        );
    };

    const resultColumns = [
        {
            titleId: 'archive.hh.results.col.hh',
            key: 'householdId'
        },
        {
            titleId: 'archive.hh.results.col.match',
            key: 'applicationYears',
            render: (item) => {
                return renderMatchCriteria(item);
            }
        },
        {
            titleId: 'archive.hh.results.col.actions',
            key: 'enrollmentYears',
            render: (item) => {
                return renderButtons(item);
            }
        }
    ];

    const SearchForm = () => {
        return (
            <>
                <div>
                    <div className='searchText'><FormattedMessage id='archive.hh.search.text' /></div>
                    <div className='searchSubText'><FormattedMessage id='archive.hh.search.text.sub' /></div>
                </div>
                <form onSubmit={handleSubmit(onSubmit)}>
                    <div className='inputBox'>
                        <div>
                            <input {...register('householdId', { required: true })} placeholder="Enter HH" />
                            {errors.householdId && <div><FormattedMessage id='common.field_required' /></div>}
                        </div>
                        <div>
                            <button className="usa-button gi-button gi-button__primary" type="submit"><FormattedMessage id='archive.hh.search.btn.search' /></button>
                        </div>
                    </div>
                </form>
            </>
        )
    };

    const SearchResults = () => {
        const isResultsFound = data && data.length > 0;

        if (isResultsFound) {
            return (
                <div>
                    <div className='searchText'>
                        <FormattedMessage id='archive.hh.search.match_hh_report' />
                    </div>
                    {
                        data ? <Table columns={resultColumns} showAllHeaders={true} data={data} /> : <NoResultsFound />
                    }
                </div>
            )
        } else {
            return <NoResultsFound />
        }
    };

    const ErrorMessage = () => {
        return (
            <div><FormattedMessage id='archive.hh.search.error' /></div>
        )
    }

    const isDataDefined = data !== undefined;

    return (
        <>
            <StyledSearchContainer>
                <SearchForm />
                {
                    isError && <ErrorMessage />
                }
                {
                    !isError && isDataDefined && <SearchResults />
                }
            </StyledSearchContainer>
        </>
    );
};

export default ArchiveHousehold;